import { createAsyncThunk } from "@reduxjs/toolkit";
import { setErrorSnackBarMessage } from "../systemSlice";
import { errorToHandler } from "../../api/error";
import { RootState } from "app/store";
import { kmsApi, kmsRpc } from "api";
import { SiteNsResource } from "common/types";
import {
  KeyManagerRpcsAddIdentityBody,
  KeyManagerRpcsAddKeyBody,
  KeyManagerRpcsAddPolicyBody,
} from "api/keymanageRpcApi";

export const fetchKMSSupportedAPIs = createAsyncThunk(
  "kms/fetchKMSSupportedAPIs",
  async (_, { rejectWithValue, getState, dispatch }) => {
    return kmsApi.api
      .api({
        headers: {
          "content-type": "application/json",
        },
      })
      .then((res) => {
        return res.data.APIs;
      })
      .catch((res) => {
        dispatch(setErrorSnackBarMessage(errorToHandler(res.error)));
        return rejectWithValue(res);
      });
  },
);

export const fetchKMSPolicies = createAsyncThunk(
  "kms/fetchKMSPolicies",
  async (
    { siteId, namespace, resourceId }: SiteNsResource,
    { rejectWithValue, getState, dispatch },
  ) => {
    const state = getState() as RootState;
    const enclave = state.kms.currentEnclave;
    return kmsRpc.api
      .listPolicies(siteId, namespace, resourceId, {
        enclave: enclave?.name,
      })
      .then((res) => {
        return res.data;
      })
      .catch((res) => {
        dispatch(setErrorSnackBarMessage(errorToHandler(res.error)));
        return rejectWithValue(res);
      });
  },
);

export const fetchKMSPolicyDetails = createAsyncThunk(
  "kms/fetchKMSPolicyDetails",
  async (
    {
      siteId,
      namespace,
      resourceId,
      policy,
    }: {
      siteId: string;
      namespace: string;
      resourceId: string;
      policy: string;
    },
    { rejectWithValue, getState, dispatch },
  ) => {
    const state = getState() as RootState;
    const enclave = state.kms.currentEnclave;
    return kmsRpc.api
      .getPolicy(siteId, namespace, resourceId, policy, {
        enclave: enclave?.name,
      })
      .then((res) => {
        return res.data;
      })
      .catch((res) => {
        dispatch(setErrorSnackBarMessage(errorToHandler(res.error)));
        return rejectWithValue(res);
      });
  },
);

export const addKMSPolicy = createAsyncThunk(
  "kms/addKMSPolicy",
  async (
    {
      siteId,
      namespace,
      resourceId,
      body,
    }: {
      siteId: string;
      namespace: string;
      resourceId: string;
      body: KeyManagerRpcsAddPolicyBody;
    },
    { rejectWithValue, getState, dispatch },
  ) => {
    const state = getState() as RootState;
    const enclave = state.kms.currentEnclave;
    return kmsRpc.api
      .addPolicy(siteId, namespace, resourceId, {
        ...body,
        enclave: enclave?.name,
      })
      .then(() => {})
      .catch((res) => {
        dispatch(setErrorSnackBarMessage(errorToHandler(res.error)));
        return rejectWithValue(res);
      });
  },
);

export const deleteKMSPolicy = createAsyncThunk(
  "kms/deleteKMSPolicy",
  async (
    {
      siteId,
      namespace,
      resourceId,
      policy,
    }: {
      siteId: string;
      namespace: string;
      resourceId: string;
      policy: string;
    },
    { rejectWithValue, getState, dispatch },
  ) => {
    const state = getState() as RootState;
    const enclave = state.kms.currentEnclave;
    return kmsRpc.api
      .deletePolicy(siteId, namespace, resourceId, policy, {
        enclave: enclave?.name,
      })
      .then(() => {})
      .catch((res) => {
        dispatch(setErrorSnackBarMessage(errorToHandler(res.error)));
        return rejectWithValue(res);
      });
  },
);

export const fetchKMSEnclaves = createAsyncThunk(
  "kms/fetchKMSEnclaves",
  async (
    { siteId, namespace, resourceId }: SiteNsResource,
    { rejectWithValue, getState, dispatch },
  ) => {
    return kmsRpc.api
      .listEnclaves(siteId, namespace, resourceId, {})
      .then((res) => {
        return res.data;
      })
      .catch((res) => {
        console.error(res);
        dispatch(setErrorSnackBarMessage(errorToHandler(res.error)));
        return rejectWithValue(res);
      });
  },
);

export const fetchKMSEnclaveDetails = createAsyncThunk(
  "kms/fetchKMSEnclaveDetails",
  async (
    {
      siteId,
      namespace,
      resourceId,
      enclave,
    }: {
      siteId: string;
      namespace: string;
      resourceId: string;
      enclave: string;
    },
    { rejectWithValue, getState, dispatch },
  ) => {
    return kmsRpc.api
      .describeEnclave(siteId, namespace, resourceId, enclave)
      .then((res) => {
        return res.data;
      })
      .catch((res) => {
        dispatch(setErrorSnackBarMessage(errorToHandler(res.error)));
        return rejectWithValue(res);
      });
  },
);

export const addKMSEnclave = createAsyncThunk(
  "kms/addKMSEnclave",
  async (
    {
      siteId,
      namespace,
      resourceId,
      name,
    }: { siteId: string; namespace: string; resourceId: string; name: string },
    { rejectWithValue, getState, dispatch },
  ) => {
    return kmsRpc.api
      .addEnclave(siteId, namespace, resourceId, { name: name })
      .then(() => {})
      .catch((res) => {
        dispatch(setErrorSnackBarMessage(errorToHandler(res.error)));
        return rejectWithValue(res);
      });
  },
);

export const deleteKMSEnclave = createAsyncThunk(
  "kms/deleteKMSEnclave",
  async (
    {
      siteId,
      namespace,
      resourceId,
      enclave,
    }: {
      siteId: string;
      namespace: string;
      resourceId: string;
      enclave: string;
    },
    { rejectWithValue, getState, dispatch },
  ) => {
    return kmsRpc.api
      .deleteEnclave(siteId, namespace, resourceId, enclave)
      .then(() => {})
      .catch((res) => {
        dispatch(setErrorSnackBarMessage(errorToHandler(res.error)));
        return rejectWithValue(res);
      });
  },
);

export const fetchKMSKeys = createAsyncThunk(
  "kms/fetchKMSKeys",
  async (
    {
      siteId,
      namespace,
      resourceId,
    }: {
      siteId: string;
      namespace: string;
      resourceId: string;
    },
    { rejectWithValue, getState, dispatch },
  ) => {
    const state = getState() as RootState;
    const enclave = state.kms.currentEnclave;
    return kmsRpc.api
      .listKeys(siteId, namespace, resourceId, {
        enclave: enclave?.name,
      })
      .then((res) => {
        return res.data.items;
      })
      .catch((res) => {
        dispatch(setErrorSnackBarMessage(errorToHandler(res.error)));
        return rejectWithValue(res);
      });
  },
);

export const fetchKMSKeyDetails = createAsyncThunk(
  "kms/fetchKMSKeyDetails",
  async (
    {
      siteId,
      namespace,
      resourceId,
      key,
    }: {
      siteId: string;
      namespace: string;
      resourceId: string;
      key: string;
    },
    { rejectWithValue, getState, dispatch },
  ) => {
    const state = getState() as RootState;
    const enclave = state.kms.currentEnclave;
    return kmsRpc.api
      .describeKey(siteId, namespace, resourceId, key, {
        enclave: enclave?.name,
      })
      .then((res) => {
        return res.data;
      })
      .catch((res) => {
        dispatch(setErrorSnackBarMessage(errorToHandler(res.error)));
        return rejectWithValue(res);
      });
  },
);

export const addKMSKey = createAsyncThunk(
  "kms/addKMSKey",
  async (
    {
      siteId,
      namespace,
      resourceId,
      body,
    }: {
      siteId: string;
      namespace: string;
      resourceId: string;
      body: KeyManagerRpcsAddKeyBody;
    },
    { rejectWithValue, getState, dispatch },
  ) => {
    const state = getState() as RootState;
    const enclave = state.kms.currentEnclave;
    return kmsRpc.api
      .addKey(siteId, namespace, resourceId, {
        ...body,
        enclave: enclave?.name,
      })
      .then(() => {})
      .catch((res) => {
        dispatch(setErrorSnackBarMessage(errorToHandler(res.error)));
        return rejectWithValue(res);
      });
  },
);

export const deleteKMSKey = createAsyncThunk(
  "kms/deleteKMSKey",
  async (
    {
      siteId,
      namespace,
      resourceId,
      key,
    }: {
      siteId: string;
      namespace: string;
      resourceId: string;
      key: string;
    },
    { rejectWithValue, getState, dispatch },
  ) => {
    const state = getState() as RootState;
    const enclave = state.kms.currentEnclave;
    return kmsRpc.api
      .deleteKey(siteId, namespace, resourceId, key, {
        enclave: enclave?.name,
      })
      .then(() => {})
      .catch((res) => {
        dispatch(setErrorSnackBarMessage(errorToHandler(res.error)));
        return rejectWithValue(res);
      });
  },
);

export const fetchKMSIdentities = createAsyncThunk(
  "kms/fetchKMSIdentities",
  async (
    {
      siteId,
      namespace,
      resourceId,
    }: {
      siteId: string;
      namespace: string;
      resourceId: string;
    },
    { rejectWithValue, getState, dispatch },
  ) => {
    const state = getState() as RootState;
    const enclave = state.kms.currentEnclave;
    return kmsRpc.api
      .listIdentities(siteId, namespace, resourceId, {
        enclave: enclave?.name,
      })
      .then((res) => {
        return res.data.items;
      })
      .catch((res) => {
        dispatch(setErrorSnackBarMessage(errorToHandler(res.error)));
        return rejectWithValue(res);
      });
  },
);

export const fetchKMSIdentityDetails = createAsyncThunk(
  "kms/fetchKMSIdentityDetails",
  async (
    {
      siteId,
      namespace,
      resourceId,
      identity,
    }: {
      siteId: string;
      namespace: string;
      resourceId: string;
      identity: string;
    },
    { rejectWithValue, getState, dispatch },
  ) => {
    const state = getState() as RootState;
    const enclave = state.kms.currentEnclave;
    return kmsRpc.api
      .describeIdentity(siteId, namespace, resourceId, identity, {
        enclave: enclave?.name,
      })
      .then((res) => {
        return res.data;
      })
      .catch((res) => {
        dispatch(setErrorSnackBarMessage(errorToHandler(res.error)));
        return rejectWithValue(res);
      });
  },
);

export const addKMSIdentity = createAsyncThunk(
  "kms/addKMSIdentity",
  async (
    {
      siteId,
      namespace,
      resourceId,
      body,
    }: {
      siteId: string;
      namespace: string;
      resourceId: string;
      body: KeyManagerRpcsAddIdentityBody;
    },
    { rejectWithValue, getState, dispatch },
  ) => {
    const state = getState() as RootState;
    const enclave = state.kms.currentEnclave;
    return kmsRpc.api
      .addIdentity(siteId, namespace, resourceId, {
        ...body,
        enclave: enclave?.name,
      })
      .then(() => {})
      .catch((res) => {
        dispatch(setErrorSnackBarMessage(errorToHandler(res.error)));
        return rejectWithValue(res);
      });
  },
);

export const deleteKMSIdentity = createAsyncThunk(
  "kms/deleteKMSIdentity",
  async (
    {
      siteId,
      namespace,
      resourceId,
      identity,
    }: {
      siteId: string;
      namespace: string;
      resourceId: string;
      identity: string;
    },
    { rejectWithValue, getState, dispatch },
  ) => {
    const state = getState() as RootState;
    const enclave = state.kms.currentEnclave;
    return kmsRpc.api
      .deleteIdentity(siteId, namespace, resourceId, identity, {
        enclave: enclave?.name,
      })
      .then(() => {})
      .catch((res) => {
        dispatch(setErrorSnackBarMessage(errorToHandler(res.error)));
        return rejectWithValue(res);
      });
  },
);

export const fetchKMSClusters = createAsyncThunk(
  "kms/fetchKMSClusters",
  async (_, { rejectWithValue, getState, dispatch }) => {
    return kmsApi.cluster
      .listClusters({}, { headers: { "content-type": "application/json" } })
      .then((res) => {
        return res.data.Names;
      })
      .catch((res) => {
        dispatch(setErrorSnackBarMessage(errorToHandler(res.error)));
        return rejectWithValue(res);
      });
  },
);

export const fetchKMSClusterDetails = createAsyncThunk(
  "kms/fetchKMSClusterDetails",
  async (name: string, { rejectWithValue, getState, dispatch }) => {
    const state = getState() as RootState;
    const enclave = state.kms.currentEnclave;
    return kmsApi.cluster
      .describeCluster(name, {
        headers: {
          "Minio-Kms-Enclave": enclave?.name!,
          "content-type": "application/json",
        },
      })
      .then((res) => {
        return res.data;
      })
      .catch((res) => {
        dispatch(setErrorSnackBarMessage(errorToHandler(res.error)));
        return rejectWithValue(res);
      });
  },
);

export const addKMSCluster = createAsyncThunk(
  "kms/addKMSCluster",
  async (params: any, { rejectWithValue, getState, dispatch }) => {
    const state = getState() as RootState;
    const enclave = state.kms.currentEnclave;
    return kmsApi.cluster
      .createCluster(
        params.name,
        {},
        {
          headers: {
            "Minio-Kms-Enclave": enclave?.name!,
            "content-type": "application/json",
          },
        },
      )
      .then(() => {})
      .catch((res) => {
        dispatch(setErrorSnackBarMessage(errorToHandler(res.error)));
        return rejectWithValue(res);
      });
  },
);

export const deleteKMSCluster = createAsyncThunk(
  "kms/deleteKMSCluster",
  async (name: string, { rejectWithValue, getState, dispatch }) => {
    const state = getState() as RootState;
    const enclave = state.kms.currentEnclave;
    return kmsApi.cluster
      .deleteCluster(name, {
        headers: {
          "Minio-Kms-Enclave": enclave?.name!,
          "content-type": "application/json",
        },
      })
      .then(() => {})
      .catch((res) => {
        dispatch(setErrorSnackBarMessage(errorToHandler(res.error)));
        return rejectWithValue(res);
      });
  },
);

export const fetchKMSDetails = createAsyncThunk(
  "kms/fetchKMSDetails",
  async (
    requestElements: { siteID: string; namespace: string; resourceID: string },
    { rejectWithValue, getState, dispatch },
  ) => {
    return kmsRpc.api
      .getKeyManager(
        requestElements.siteID,
        requestElements.namespace,
        requestElements.resourceID,
      )
      .then((res) => res.data)
      .catch((res) => {
        dispatch(setErrorSnackBarMessage(errorToHandler(res.error)));
        return rejectWithValue(res);
      });
  },
);

export const deleteKMSInstance = createAsyncThunk(
  "kms/deleteKMSInstance",
  async (
    requestElements: { siteID: string; namespace: string; resourceID: string },
    { rejectWithValue, getState, dispatch },
  ) => {
    return kmsRpc.api
      .deleteKeyManager(
        requestElements.siteID,
        requestElements.namespace,
        requestElements.resourceID,
      )
      .then(() => {})
      .catch((res) => {
        dispatch(setErrorSnackBarMessage(errorToHandler(res.error)));
        return rejectWithValue(res);
      });
  },
);
